import * as React from "react";
import {cx} from "emotion";

import "./keyboard-nav.scss";

import {cs}           from "@Common/react/chain-services";
import {fragments}    from "@Common/react/fragments";
import {Repeating}    from "@Common/react/repeating";
import {Static}       from "@Common/react/static";
import {Static2}      from "@Common/react/static-2";
import {UseState}     from "@Common/react/use-state";
import {isSpecialKey} from "@Common/utils/browser/is-special-key";
import {binarySearch} from "@Common/utils/search/binary-search";

import {GlobalEventQueue}      from "../../../../../browser/global-event-queue";
import {OnGlobalKeyDownEnter}  from "../../../../../browser/global-key-down-enter";
import {OnGlobalKeyDownEscape} from "../../../../../browser/global-key-down-escape";
import {isDevQuan}             from "../../../../../debug/is-dev-quan";

export const KeyboardNav = ({getList, initFocus, onSelect, next}) => cs(
    ["filtering", (_, next) => UseState({
        // getInitValue: () => isDevQuan() && "north ",
        next,
    })],
    ["focus", (_, next) => UseState({initValue: initFocus === -1 ? null : initFocus, next})],
    ["filteredIndexList", ({filtering}, next) => next(
        !filtering.value ? (
            null
        ) : (
            getList().map((e, i) => e.indexOf(filtering.value.toLowerCase()) > -1 ? i : null).filter((i) => i != null)
        )
    )],
    ["parentRef", (_, next) => Static2({next})],
    ["itemRefs", (_, next) => Static({getInitValue: () => ({}), next})],
    ({filtering, filteredIndexList, focus, itemRefs, parentRef}) => fragments(
        next({
            isFocused: (index) => index === focus.value,
            renderFilter: () => filtering.value && (
                <div className="keyboard-nav-4ei filter">
                    <i className="fa fa-search"/>
                    <div className={cx("text", {error: !filteredIndexList?.length})}>
                        {filtering.value}
                    </div>
                </div>
            ),
            parentRef: parentRef.set,
            itemRef: (i) => (dom) => itemRefs[i] = dom,
            renderItem: (text, {highlightClassName}) => {
                if (!filtering.value) {
                    return text;
                }
                const index = text.toLowerCase().indexOf(filtering.value.toLowerCase());
                return index === -1 ? text : fragments(
                    text.substring(0, index),
                    <div className={highlightClassName}>
                        <div className="text">
                            {text.substring(index, index + filtering.value.length)}
                        </div>
                    </div>,
                    text.substring(index + filtering.value.length),
                );
            },
        }),

        OnGlobalKeyDownAlphabet({
            action: (e) => filtering.change((filter) => (filter??"") + e.key),
        }),
        OnGlobalKeyDownSpace({
            action: () => filtering.change((filter) => (filter??"") + " "),
        }),
        OnGlobalKeyDownArrowUp({
            action: () => focus.change((f) => !f ? 0 : f-1),
        }),
        OnGlobalKeyDownArrowDown({
            action: () => focus.change((f) => f==null ? 0 : f < getList().length - 1 ? f + 1 : f),
        }),
        focus.value != null && OnGlobalKeyDownEnter({
            action: () => onSelect(focus.value),
        }),

        Repeating({
            watchValue: focus.value,
            action: (v) => {
                if (v == null) {
                    return;
                }
                const parentDom = parentRef.get();
                const itemDom = itemRefs[v];
                const itemRect = itemDom.getBoundingClientRect();
                const parentRect = parentDom.getBoundingClientRect();
                if (itemRect.y < parentRect.y) {
                    parentDom.scrollTop -= parentRect.y - itemRect.y + 20;
                } else if (itemRect.y + itemRect.height > parentRect.y + parentRect.height) {
                    parentDom.scrollTop += (itemRect.y + itemRect.height) - (parentRect.y + parentRect.height) + 20;
                }
            },
        }),
        filtering.value && fragments(
            Repeating({
                watchValue: filteredIndexList?.length ?? 0,
                action: (v) => {
                    if (!v) {
                        return;
                    }
                    if (focus.value == null) {
                        focus.onChange(filteredIndexList[0]);
                    }

                    if (filteredIndexList.includes(focus.value)) {
                        return;
                    }
                    const newIndex = -binarySearch(focus.value, {col: filteredIndexList}) - 1;
                    focus.onChange(filteredIndexList[newIndex === filteredIndexList.length ? 0 : newIndex]);
                },
            }),
            OnGlobalKeyDownEscape({
                action: () => filtering.onChange(null),
            }),
            OnGlobalKeyDownBackspace({
                action: () => filtering.change((filter) => !filter ? null : filter.substring(0, filter.length - 1)),
            }),

            OnGlobalKeyDownArrowUp({
                action: () => focus.change((f) => {
                    const index = filteredIndexList.indexOf(f);
                    return filteredIndexList[roundRobin(index - 1, filteredIndexList.length)];

                }),
            }),
            OnGlobalKeyDownArrowDown({
                action: () => focus.change((f) => {
                    const index = filteredIndexList.indexOf(f);
                    return filteredIndexList[roundRobin(index + 1, filteredIndexList.length)];
                }),
            }),
        ),
    ),
);

const roundRobin = (index, length) => (
    index === length ? (
        0
    ) : index === -1 ? (
        length - 1
    ) : (
        index
    )
);

export const OnGlobalKeyDownAlphabet = GlobalEventQueue({
    eventName: "keydown",
    surCheck: (e) => isAlphabet(e.key) && !isSpecialKey(e),
});
export const OnGlobalKeyDownSpace = GlobalEventQueue({
    eventName: "keydown",
    surCheck: (e) => e.key === " " && !isSpecialKey(e),
});
export const OnGlobalKeyDownBackspace = GlobalEventQueue({
    eventName: "keydown",
    surCheck: (e) => e.key === "Backspace" && !isSpecialKey(e),
});
export const OnGlobalKeyDownArrowUp = GlobalEventQueue({
    eventName: "keydown",
    surCheck: (e) => e.key === "ArrowUp"   && !isSpecialKey(e),
});
export const OnGlobalKeyDownArrowDown = GlobalEventQueue({
    eventName: "keydown",
    surCheck: (e) => e.key === "ArrowDown" && !isSpecialKey(e),
});

const isAlphabet = (k) => {
    if (k.length > 1) {
        return false;
    }
    const code = k.charCodeAt(0);
    return (
        code >= 97 && code <= 122 ||
        code >= 65 && code <= 90
    );
};
